import { RefObject, useEffect, useState } from "react";
import Hls from "hls.js";

/**
 * Attaches a streaming recording URL to a <video> element and tracks buffering.
 *
 * - **HLS** (`.m3u8`): played via `hls.js` where supported, or native HLS on
 *   Safari. This lets the player start on the first segment and seek via range
 *   requests instead of downloading the whole file.
 * - **Progressive MP4** (range-capable URL): set directly on the element — the
 *   browser streams it natively (HTTP range), so playback starts on the first
 *   buffered bytes and seeking doesn't re-download from byte 0.
 *
 * Returns `buffering` so the caller can show a loading/buffering indicator while
 * the stream loads or re-buffers.
 */

const isHlsUrl = (url: string): boolean => /\.m3u8(\?|#|$)/i.test(url);

export function useHlsVideo(
    videoRef: RefObject<HTMLVideoElement | null>,
    src: string | null | undefined
): { buffering: boolean } {
    const [buffering, setBuffering] = useState<boolean>(false);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !src) {
            setBuffering(false);
            return;
        }

        let hls: Hls | null = null;

        if (isHlsUrl(src)) {
            if (video.canPlayType("application/vnd.apple.mpegurl")) {
                // Native HLS (Safari / iOS) — no extra library needed.
                video.src = src;
            } else if (Hls.isSupported()) {
                hls = new Hls({ enableWorker: true });
                hls.loadSource(src);
                hls.attachMedia(video);
                hls.on(Hls.Events.ERROR, (_event, data) => {
                    // A fatal hls.js error (expired/403 manifest, etc.) won't
                    // recover — drop the buffering overlay so the failure shows.
                    if (data.fatal) setBuffering(false);
                });
            } else {
                // Last resort: let the element try the URL directly.
                video.src = src;
            }
        } else {
            // Progressive / range-capable URL — native streaming via the element.
            video.src = src;
        }

        const onWaiting = () => setBuffering(true);
        const onLoadStart = () => setBuffering(true);
        const onStreamReady = () => setBuffering(false);
        // Native <video> error (expired/private URL, decode failure) — never
        // leave the overlay spinning on a path that won't recover.
        const onError = () => setBuffering(false);

        video.addEventListener("loadstart", onLoadStart);
        video.addEventListener("waiting", onWaiting);
        video.addEventListener("seeking", onWaiting);
        video.addEventListener("canplay", onStreamReady);
        video.addEventListener("playing", onStreamReady);
        video.addEventListener("seeked", onStreamReady);
        video.addEventListener("error", onError);

        return () => {
            video.removeEventListener("loadstart", onLoadStart);
            video.removeEventListener("waiting", onWaiting);
            video.removeEventListener("seeking", onWaiting);
            video.removeEventListener("canplay", onStreamReady);
            video.removeEventListener("playing", onStreamReady);
            video.removeEventListener("seeked", onStreamReady);
            video.removeEventListener("error", onError);
            if (hls) {
                hls.destroy();
            } else {
                // Release the native source so a URL change doesn't keep buffering the old stream.
                video.removeAttribute("src");
                video.load();
            }
        };
    }, [videoRef, src]);

    return { buffering };
}
